import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { AlertCircle, AlertTriangle, Info } from 'lucide-react'
import { cx } from './cx'
import s from './Notice.module.css'

export type NoticeTone = 'info' | 'warn' | 'critical'

export interface NoticeProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /**
   * THE ELEMENT, because an alert is a thing a host has to FOCUS.
   *
   * `HTMLAttributes` carries no `ref`, and that omission is what kept
   * `RelationshipWorkspace` on its own `.notice` paragraph: the workspace moves
   * focus onto its announcement so a keyboard reader lands on the refusal
   * instead of hunting for it (`RelationshipWorkspace.tsx`'s `errorRef`). A
   * primitive that cannot be focused cannot replace a notice that must be, so
   * the type said "adopt me" and the component said no.
   *
   * React 19 passes `ref` to a function component as an ordinary prop, so
   * naming it here is the whole fix — no `forwardRef` wrapper.
   */
  ref?: Ref<HTMLDivElement>
  tone?: NoticeTone
  /**
   * Whether this notice announces itself, and how loudly.
   *
   * `status` is a polite live region: the surface is degraded but working, and
   * the reader is told when they next pause. `alert` interrupts, and is for a
   * notice that is urgent or blocking — a refusal that stopped a write.
   *
   * The default is NEITHER, deliberately. A line that merely explains what a
   * mode does should not interrupt a screen reader mid-task, and three of the
   * five host copies this replaces were informative rather than announcing.
   */
  live?: 'status' | 'alert'
  /** Overrides the tone's own mark. Rarely wanted; the default carries meaning. */
  icon?: ReactNode
  /** A dismiss, a retry, a "show me" — kept out of the text flow. */
  actions?: ReactNode
  children: ReactNode
}

/** One mark per tone, and three DIFFERENT marks — `Field.module.css` records
 *  why: two states drawn with one glyph are one state as far as a reader is
 *  concerned. `aria-hidden` because the tone is carried by the text and, when
 *  asked for, by the live region; a named icon would be read twice. */
const MARK: Record<NoticeTone, ReactNode> = {
  info: <Info size={16} aria-hidden />,
  warn: <AlertTriangle size={16} aria-hidden />,
  critical: <AlertCircle size={16} aria-hidden />,
}

/**
 * THE INLINE NOTICE — a line beside a surface that is still working (#2406).
 *
 * Five host copies existed and no primitive did: `crm.module.css`'s `.modbar`,
 * `RelationshipWorkspace`'s `.createNote` / `.review` / `.notice`, and
 * `ListView`'s degraded-lens line, which had no treatment at all and borrowed a
 * muted helper class — so a message about the surface not doing what was asked
 * read as chrome.
 *
 * NOT `ErrorState` or `EmptyState`. Those are centred plates that stand in FOR
 * content, with a glyph, a heading and a retry. A notice sits BESIDE content
 * that is still there. Routing a degradation line through `ErrorState` would
 * replace the thing it is annotating.
 *
 * The contrast pair is the kit's, and adopting it FIXES one of the five:
 * `.modbar` painted `--warn` text on `--warn-bg`, which `Pill.module.css:18-24`
 * measured at 4.46:1 and rejected. Every tone here keeps its colour in the fill
 * and the outline, and the text at the theme's foreground.
 */
export function Notice({ tone = 'info', live, icon, actions, className, children, ref, ...rest }: NoticeProps) {
  return (
    <div
      ref={ref}
      className={cx(s.notice, s[tone], className)}
      role={live === 'alert' ? 'alert' : live === 'status' ? 'status' : undefined}
      data-tone={tone}
      {...rest}
    >
      <span className={s.glyph}>{icon ?? MARK[tone]}</span>
      <div className={s.body}>{children}</div>
      {actions != null && <div className={s.actions}>{actions}</div>}
    </div>
  )
}
